import * as React from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { Switch } from 'elc-switch';
import { CheckBox } from 'elc-checkbox';
import { Dropdown } from 'elc-dropdown';
import { Body2 } from 'elc-typography';
import { ITranslationsCollection } from 'elc-service';
import { SUBSCRIBE_BUTTON, AUTO_REPLENISH_VIEW } from '../../constants/DataTestIdAttributes';
import { TOGGLE_TYPE, INFO_POPUP_TRIGGER } from '../../constants/AutoReplenish';
import { AutoReplenishDropdownTheme } from '../../../theme/default-theme';
import { classes } from '../../utils/Classes';
import { handleKeyPress, ACCEPTABLE_KEYS_PRESSED } from '../../utils/KeyPressHandler';
import { ProductAutoReplenishInfoModal } from './ProductAutoReplenishInfoModal';

export interface IProductAutoReplenish {
    className?: string;
    selectedOption: number;
    autoReplenishOptions: number[];
    isSelected: boolean;
    translations?: ITranslationsCollection;
    onSelect(option: number): void;
    toggleAutoReplenishSelection(): void;
    setAutoReplenishSelection(isSelected: boolean): void;
    switchElement?: React.ReactElement;
    autoReplenishToggleType?: string;
    autoReplenishmentConfig?: IProductAutoReplenishOptions;
}

// TODO: eventually, all AR options should be moved here
export interface IProductAutoReplenishOptions {
    enabled?: boolean;
    intervals?: number[];
    toggleType?: string;
    infoPopupTriggerType?: INFO_POPUP_TRIGGER;
    infoPopupNodeId?: number;
    overrides?: IProductAutoReplenishOverrides;
}

interface IProductAutoReplenishOverrides {
    productFull?: IProductAutoReplenishOptions;
    productQuickshop?: IProductAutoReplenishOptions;
}

export interface IIsAutoReplenishSelected {
    isAutoReplenishSelected: boolean;
}

export interface IFocus {
    isFocused: boolean;
}

const ProductAutoReplenishWrapper = styled.div`
    flex-direction: column;
    align-items: flex-start;
`;

const SubscribeWrapper = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    width: 100%;
`;

const SubscribeCopyWrapper = styled.div`
    display: block !important;
`;

const SwitchWrapper = styled.div`
    display: flex;
    flex-direction: column;
    justify-content: center;
`;

const AutoReplenishDropdown = styled(Dropdown)`
    opacity: ${(props: IIsAutoReplenishSelected) => (props.isAutoReplenishSelected ? 1 : 0.5)};
    transition: opacity 1s ease;
    z-index: 999;
    ${AutoReplenishDropdownTheme};
    .elc-dropdown-options {
        z-index: 999;
    }
`;

const AutoReplenishDropdownWrapper = styled.div`
    display: block;
`;

const AutoReplenishNonToggleableArea = styled.div`
    display: inline-block;
`;

export const ProductAutoReplenish = observer((props: IProductAutoReplenish) => {
    const {
        className,
        onSelect,
        selectedOption,
        isSelected,
        autoReplenishOptions,
        translations,
        autoReplenishToggleType,
        setAutoReplenishSelection,
        toggleAutoReplenishSelection,
        autoReplenishmentConfig
    } = props;
    const {
        autoReplenishLabel,
        selectFrequencyLabel,
        subscribeCopyLabel,
        subscribeLabel
    } = translations as ITranslationsCollection;

    const productAutoReplenishClassnames = classes('product-auto-replenish', className);
    const autoReplenishSubscribeWrapperClassnames = classes('auto-replenish-subscribe-wrapper');
    const autoReplenishSubscribeCopyWrapperClassnames = classes(
        'auto-replenish-subscribe-copy-wrapper'
    );
    const autoReplenishDropdownWrapperClassnames = classes('auto-replenish-dropdown-wrapper');
    const autoReplenishDropdownClassNames = classes('auto-replenish-dropdown');

    const intervals =
        (autoReplenishmentConfig ? autoReplenishmentConfig.intervals : autoReplenishOptions) || [];

    const dropdownOptions = intervals.map(option => ({
        label: autoReplenishLabel.replace(/::DAYS::/gi, option.toString()),
        value: option.toString()
    }));

    const handleOnChangeDropDown = (inputName: string, val: string) => {
        onSelect(parseInt(val, 10));
    };
    const stopClickPropagation = (event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
    };

    let selectedValue: string;
    if (selectedOption === 0) {
        selectedValue = selectFrequencyLabel;
    } else {
        selectedValue = autoReplenishLabel.replace(/::DAYS::/gi, selectedOption.toString());
    }

    const toggleAutoReplenish = (isChecked: boolean) => {
        props.setAutoReplenishSelection(!isChecked);
    };

    const toggleType = autoReplenishmentConfig
        ? autoReplenishmentConfig.toggleType
        : autoReplenishToggleType;

    const renderSelectorType = () => {
        switch (toggleType) {
            case TOGGLE_TYPE.CHECKBOX:
                return (
                    <CheckBox
                        label={subscribeLabel}
                        value={'auto-replenish'}
                        onChange={toggleAutoReplenish}
                        isChecked={isSelected}
                    />
                );
            case TOGGLE_TYPE.SWITCH:
            default:
                return (
                    <>
                        <Body2>{subscribeLabel}</Body2>
                        <SwitchWrapper>
                            <Switch
                                isSelected={isSelected}
                                onToggle={setAutoReplenishSelection}
                                data-test-id={SUBSCRIBE_BUTTON}
                                title={subscribeLabel}
                            />
                        </SwitchWrapper>
                    </>
                );
        }
    };

    const acceptedKeys = [ACCEPTABLE_KEYS_PRESSED.ENTER, ACCEPTABLE_KEYS_PRESSED.SPACEBAR];

    return (
        <ProductAutoReplenishWrapper
            className={productAutoReplenishClassnames}
            onClick={toggleAutoReplenishSelection}
            onKeyPress={keyPressed => {
                handleKeyPress(keyPressed, acceptedKeys, toggleAutoReplenishSelection);
            }}
            tabIndex={0}
            aria-label={selectedValue}
        >
            <SubscribeWrapper className={autoReplenishSubscribeWrapperClassnames}>
                {renderSelectorType()}
            </SubscribeWrapper>
            <SubscribeCopyWrapper className={autoReplenishSubscribeCopyWrapperClassnames}>
                {subscribeCopyLabel}
            </SubscribeCopyWrapper>
            <AutoReplenishDropdownWrapper className={autoReplenishDropdownWrapperClassnames}>
                <AutoReplenishNonToggleableArea onClick={stopClickPropagation}>
                    <AutoReplenishDropdown
                        className={autoReplenishDropdownClassNames}
                        isAutoReplenishSelected={isSelected}
                        options={dropdownOptions}
                        name={'autoReplenishOptions'}
                        selectedValue={selectedValue}
                        dataTestId={AUTO_REPLENISH_VIEW}
                        onChange={handleOnChangeDropDown}
                        isDisabled={!isSelected}
                    />
                </AutoReplenishNonToggleableArea>
                {autoReplenishmentConfig && autoReplenishmentConfig.enabled && (
                    <ProductAutoReplenishInfoModal config={autoReplenishmentConfig} />
                )}
            </AutoReplenishDropdownWrapper>
        </ProductAutoReplenishWrapper>
    );
});
